import { get, post } from './client';
import { getPromoById, getPromos, getPromoShopItems, getRewardsOfItem } from './atlantis.api';

// The waitlist-svc operator surface (TRU-1066/1079). CONTRACT NOTE: the
// service's admin routes are parked in Venom's follow-up series (the #57
// walking-skeleton bar, 2026-08-12); this module names the TARGET contract
// the console builds against, and the local mock serves it for dev
// (Vault scratchpad waitlist-mock-svc.mjs). Deltas the follow-up series
// owes beyond the parked routes: reversible enabled/disabled on codes and
// batches, start-disabled minting, single + word-list mint modes, and the
// admin read routes (codes / entries / audit list).
const BASE_URL = process.env.REACT_APP_WAITLIST_URL ?? 'http://127.0.0.1:8790/v1/waitlist';

/** Promo-binding v2 (2026-08-27): the picker scopes every admin route with
 *  ?waitlistId=<promoId>. The old backend ignores the extra param (harmless
 *  — the panel ships first, the svc scoping lands with phase C2); the new
 *  backend reads it over its WAITLIST_ID env fallback. */
const scoped = (path, waitlistId) =>
  waitlistId
    ? `${path}${path.includes('?') ? '&' : '?'}waitlistId=${encodeURIComponent(waitlistId)}`
    : path;

export const getWaitlistCodes = async (Authorization = '', waitlistId = undefined) => {
  const res = await get(scoped(`${BASE_URL}/admin/codes`, waitlistId), { Authorization });
  return res?.codes ?? [];
};

export const getWaitlistEntries = async (Authorization = '', waitlistId = undefined) => {
  const res = await get(scoped(`${BASE_URL}/admin/entries`, waitlistId), { Authorization });
  return res?.entries ?? [];
};

export const getWaitlistAudit = async (Authorization = '', waitlistId = undefined) => {
  const res = await get(scoped(`${BASE_URL}/admin/audit`, waitlistId), { Authorization });
  return res?.actions ?? [];
};

/**
 * One mint endpoint, three modes (the v3 design):
 *  { mode: 'single', code, maxUses, ownerLabel, classLabel, startDisabled }
 *  { mode: 'mass', prefix, count, usesPerCode, ownerLabel, classLabel, startDisabled }
 *  { mode: 'wordlist', codes: [..], usesPerCode, ownerLabel, classLabel, startDisabled }
 * Answers { batchId, report: { created, skippedExisting, rejected: [{code, reason}] } }.
 */
export const mintWaitlistCodes = async (body, Authorization = '', waitlistId = undefined) => {
  const res = await post(scoped(`${BASE_URL}/admin/code-batches`, waitlistId), body, {
    Authorization,
  });
  return res?.data;
};

/** Reversible, per code. */
export const setWaitlistCodeEnabled = async (code, enabled, Authorization = '') => {
  const res = await post(
    `${BASE_URL}/admin/codes/${encodeURIComponent(code)}/enabled`,
    { enabled },
    { Authorization }
  );
  return res?.data;
};

/** Reversible, per batch — the leaked-code lever. */
export const setWaitlistBatchEnabled = async (batchId, enabled, Authorization = '') => {
  const res = await post(
    `${BASE_URL}/admin/code-batches/${encodeURIComponent(batchId)}/enabled`,
    { enabled },
    { Authorization }
  );
  return res?.data;
};

/** BULK FREEZE (Admin via Gwen, 2026-08-24): the wallet-list sweep rides
 *  the SAME route — {walletIds, state, reason} in, {updated, missing} out;
 *  unmatched wallets come back by name. */
export const bulkWaitlistEntryState = async (
  { walletIds, state, reason },
  Authorization = '',
  waitlistId = undefined
) => {
  const res = await post(
    scoped(`${BASE_URL}/admin/entries/state`, waitlistId),
    { walletIds, state, reason },
    { Authorization }
  );
  return res?.data;
};

/** Grant Jelly: banded admin drops. Same batchId across chunks = exactly-once. */
export const grantWaitlistJelly = async (
  { batchId, bands },
  Authorization = '',
  waitlistId = undefined
) => {
  const res = await post(
    scoped(`${BASE_URL}/admin/entries/grant-coins`, waitlistId),
    { batchId, bands },
    { Authorization }
  );
  return res?.data;
};

/** Freeze / unfreeze with the mandatory reason (ADJ audit row). */
export const setWaitlistEntryState = async (
  { cognitoSub, state, reason },
  Authorization = '',
  waitlistId = undefined
) => {
  const res = await post(
    scoped(`${BASE_URL}/admin/entries/state`, waitlistId),
    { cognitoSub, state, reason },
    { Authorization }
  );
  return res?.data;
};

/** The offline-pipeline CSV (attribution, checkmarks, coins, fruit,
 *  fingerprint hashes) — produced server-side, downloaded as-is. */
export const getWaitlistEntriesCsv = async (Authorization = '', waitlistId = undefined) => {
  const res = await get(scoped(`${BASE_URL}/admin/export`, waitlistId), { Authorization });
  return typeof res === 'string' ? res : res?.csv ?? '';
};

/** One page of the anti-sybil report: wallet + fingerprint keys as a CSV
 *  fragment (header on the first page) plus the cursor for the next. */
export const getWaitlistAntisybilPage = async (
  Authorization = '',
  waitlistId = undefined,
  cursor = undefined
) => {
  let path = `${BASE_URL}/admin/export?report=antisybil`;
  if (cursor) path += `&cursor=${encodeURIComponent(cursor)}`;
  const res = await get(scoped(path, waitlistId), { Authorization });
  return { csv: res?.csv ?? '', nextCursor: res?.nextCursor ?? null };
};

/** The waitlist row (P9): dates + the prize ladder. Every save audits. */
export const getWaitlistSettings = async (Authorization = '', waitlistId = undefined) => {
  const res = await get(scoped(`${BASE_URL}/admin/settings`, waitlistId), { Authorization });
  return res?.settings ?? null;
};

/** The whole /admin/settings answer in ONE call: the settings row plus the
 *  EFFECTIVE last-wave id (the server returns null when the mark is stale —
 *  a newer wave opened or closed — so no stale-mark logic lives here). An
 *  older backend omits lastWaveId: missing reads as null (not marked). */
export const getWaitlistSettingsWithLastWave = async (
  Authorization = '',
  waitlistId = undefined
) => {
  const res = await get(scoped(`${BASE_URL}/admin/settings`, waitlistId), { Authorization });
  return { settings: res?.settings ?? null, lastWaveId: res?.lastWaveId ?? null };
};

/** Mark / unmark a wave as the LAST wave ("no more waves are coming"). Only
 *  the open wave, or the most recently closed wave while no wave is open, can be marked; otherwise
 *  the server answers 400 with its message (an older backend answers 400
 *  "intent must be snapshot or close"). Never touches Close + Grant. */
export const setWaitlistLastWave = async (
  waveId,
  lastWave,
  Authorization = '',
  waitlistId = undefined
) => {
  const res = await post(
    scoped(`${BASE_URL}/admin/waves`, waitlistId),
    { waveId, intent: 'last-wave', lastWave },
    { Authorization }
  );
  return res?.data;
};

/** Promo-binding v2 + waitlist-svc Track C: an OPEN (create) must carry
 *  shopPromoId and rfpCampaignId — the wave seals both at creation and
 *  the seals never move. The grant reads this wave's campaign (not a
 *  later promo re-bind). The caller puts them in the body; the server's
 *  own refusals ("A wave must name its waitlist promo", "A wave must
 *  name its Cap campaign", "the seal never moves", ...) surface verbatim
 *  upstream. An EDIT may repeat the sealed rfpCampaignId; it must never
 *  silently drop a create-time campaign. */
export const saveWaitlistSettings = async (body, Authorization = '', waitlistId = undefined) => {
  const res = await post(scoped(`${BASE_URL}/admin/settings`, waitlistId), body, { Authorization });
  return res?.data;
};

/** Close + Grant: pays the wave from the cutoff snapshot and closes it so
 *  the next wave can open. Cutoff / Freeze scores is automatic at closesAt —
 *  there is no separate admin cutoff endpoint. Replay-safe (an already-closed
 *  wave returns its standing report). */
export const closeWaitlistWave = async (waveId, Authorization = '', waitlistId = undefined) => {
  const res = await post(
    scoped(`${BASE_URL}/admin/waves`, waitlistId),
    { waveId },
    { Authorization }
  );
  return res?.data;
};

export const getWaitlistWaveReport = async (waveId, Authorization = '', waitlistId = undefined) => {
  // get() already unwraps to the body: { report, rows } straight from the
  // service (or the mock; same shape).
  const res = await get(
    scoped(`${BASE_URL}/admin/waves/${encodeURIComponent(waveId)}`, waitlistId),
    {
      Authorization,
    }
  );
  return res ?? null;
};

export const getWaitlistPrizeItems = async (Authorization = '', promoIdArg = undefined) => {
  const promoId = promoIdArg;
  if (!promoId) {
    return {
      items: [],
      error: 'Pick a waitlist above. The panel needs the WAITLIST promo the grant pays.',
    };
  }
  try {
    const promo = await getPromoById(promoId, Authorization);
    if (!promo) {
      // No raw ids in admin-facing copy (Gwen 2026-08-27) — a stale baked
      // id or a deleted promo reads as a human sentence, not a uuid.
      return {
        items: [],
        error: 'No waitlist promo found — create one in Promotions, then pick it above.',
      };
    }
    if (promo.promoType !== 'WAITLIST') {
      return { items: [], error: `Promo ${promoId} is ${promo.promoType}, not WAITLIST.` };
    }
    const shopItems = (await getPromoShopItems(promoId, Authorization)) ?? [];
    const items = await Promise.all(
      shopItems.filter(Boolean).map(async item => {
        const rewards = (await getRewardsOfItem(promoId, item.itemId, Authorization)) ?? [];
        const alwaysWins =
          rewards.length === 1 && rewards[0]?.pct === 1 && rewards[0]?.unlimited === true;
        return {
          itemId: item.itemId,
          name: item.name ?? item.itemId,
          rewardLabel: item.rewardLabel,
          alwaysWins,
          disabled: item.disabled === true,
          archived: item.archived === true,
        };
      })
    );
    return {
      items,
      error: null,
      // The promo's window rides along: waves must FIT inside it, and the
      // form shows the range (create anytime — active status never gates).
      promoWindow: { startTime: promo.startTime, endTime: promo.endTime, name: promo.promoName },
    };
  } catch (err) {
    return {
      items: [],
      error: `Atlantis is unreachable or refused: ${
        err?.response?.status ?? err?.message ?? 'unknown error'
      }`,
    };
  }
};

/** The picker's options: every atlantis promo of type WAITLIST, newest
 *  window first. Creating the promo effectively creates the waitlist —
 *  its id IS the waitlist id (promo-binding v2). */
/** The WAITLIST promos, newest first, plus whether the lookup itself failed.
 *  An Atlantis outage answers `{ promos: [], failed: true }`, which is not
 *  the same thing as a stage with no promos — the panel must not call the
 *  stage empty on a list it never got. */
export const listWaitlistPromos = async (Authorization = '') => {
  try {
    const promos = (await getPromos(Authorization)) ?? [];
    return {
      promos: promos
        .filter(promo => promo?.promoType === 'WAITLIST')
        .sort((a, b) => String(b.startTime ?? '').localeCompare(String(a.startTime ?? ''))),
      failed: false,
    };
  } catch (err) {
    console.error('Waitlist promo list failed:', err);
    return { promos: [], failed: true };
  }
};

export const getWaitlistPromos = async (Authorization = '') =>
  (await listWaitlistPromos(Authorization)).promos;
